/**
 * Recently Viewed Store - Pinia store for recently viewed products
 * Manages viewing history for both authenticated and anonymous users
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import type { RecentlyViewedProduct } from '@/types'
import { getErrorMessage } from '@/utils/errorUtils'

export const useRecentlyViewedStore = defineStore('recentlyViewed', () => {
  // State
  const items = ref<RecentlyViewedProduct[]>([])
  const loading = ref(false)
  const error = ref('')

  // Getters
  const hasItems = computed(() => items.value.length > 0)

  function recentItems(limit: number): RecentlyViewedProduct[] {
    return items.value.slice(0, limit)
  }

  // Actions
  async function fetchRecentlyViewed(limit: number = 20): Promise<RecentlyViewedProduct[]> {
    loading.value = true
    error.value = ''

    try {
      const response = await api.recentlyViewed.list(limit)
      items.value = response.data || []
      return items.value
    } catch (err: unknown) {
      error.value = getErrorMessage(err)
      return []
    } finally {
      loading.value = false
    }
  }

  async function removeItem(id: string): Promise<void> {
    try {
      await api.recentlyViewed.remove(id)
      items.value = items.value.filter(item => String(item.id) !== String(id))
    } catch (err: unknown) {
      error.value = getErrorMessage(err)
      throw err
    }
  }

  async function clearAll(): Promise<void> {
    try {
      await api.recentlyViewed.clearAll()
      items.value = []
    } catch (err: unknown) {
      error.value = getErrorMessage(err)
      throw err
    }
  }

  return {
    // State
    items,
    loading,
    error,
    // Getters
    hasItems,
    recentItems,
    // Actions
    fetchRecentlyViewed,
    removeItem,
    clearAll,
  }
})
